import { SectionNav, type SectionNavProps } from '@molecules/SectionNav'

import { ChoosingNavLevel } from '../ChoosingNavLevel'
import type { ComponentDoc, PropDocs } from '../types'

const props: PropDocs<SectionNavProps> = {
  items: {
    type: '{ label: string; href: string; current?: boolean }[]',
    required: true,
    description: 'One entry per page in the section. Mark the page being shown with current.',
  },
  ariaLabel: {
    type: 'string',
    required: true,
    description: 'Names the nav landmark after the page header tab it sits under, for example "Configurations".',
  },
  linkComponent: {
    type: 'ElementType',
    default: "'a'",
    description: "What each link renders as. Pass Inertia's Link.",
  },
}

const items = [
  { label: 'General', href: '#general', current: true },
  { label: 'NationBuilder', href: '#nationbuilder' },
  { label: 'Mailers', href: '#mailers' },
]

const doc: ComponentDoc<SectionNavProps> = {
  slug: 'section-nav',
  name: 'SectionNav',
  tier: 'molecule',
  description:
    'A list of links down the left of a page body, for a third level of navigation under a page header tab. Each link is a real page with its own URL. On narrow screens the list wraps above the content.',
  importStatement: "import { SectionNav } from '@molecules/SectionNav'",
  sourcePath: 'app/frontend/components/molecules/SectionNav.tsx',
  component: SectionNav,
  props,
  examples: [
    {
      title: 'Configurations sections',
      render: () => (
        <div className="tw:flex tw:w-full tw:flex-col tw:gap-6 tw:md:flex-row">
          <SectionNav ariaLabel="Configurations" items={items} />
          <div className="tw:h-32 tw:grow tw:rounded-xl tw:border tw:border-gray-cool-200 tw:bg-white" />
        </div>
      ),
      code: `<div className="tw:flex tw:flex-col tw:gap-6 tw:md:flex-row tw:md:items-start">
  <SectionNav ariaLabel={sections.label} items={sections.items} linkComponent={Link} />
  <div className="tw:min-w-0 tw:grow">{children}</div>
</div>`,
    },
  ],
  usage: {
    do: [
      'Use it only under a page header nav, when the current tab holds several pages of its own.',
      'Put it in a flex row beside the page content, as ConfigurationsLayout does.',
      'Build the items on the server, so every page in the section shows the same list.',
    ],
    dont: [
      'Use it in place of the main sidebar or the page header nav. Links go at the highest level their importance earns.',
      'Use it for switching content within one page. Use SegmentedControl.',
      'Render it with one item.',
    ],
  },
  choosing: { title: 'Sidebar, page header nav or section nav?', content: <ChoosingNavLevel /> },
  a11y: [
    'It is a nav landmark named by ariaLabel, so screen readers can jump to it.',
    'The current link carries aria-current="page" and is marked by a background as well as colour.',
  ],
}

export default doc
